import { useEffect, useMemo, useRef } from "react";
import { useSubscription } from "urql";
import { updateFiltersViaSubscription } from "@/store/share-of-voice/filters/shareOfVoice-filters-async.actions";
import { useShareOfVoiceParamsContextRequired } from "@/store/share-of-voice/params/shareOfVoice-params.context";
import {
  ShareVoiceSidebarFilterSubscription,
  type ShareVoiceSidebarSubscriptionVariables,
} from "./shareOfVoice-sidebar-subscriptions";

interface UseShareVoiceFilterSubscriptionProps {
  categories?: ShareVoiceSidebarSubscriptionVariables["categories"];
  enabled?: boolean;
}

/**
 * Stable context object — avoids creating a new reference every render
 * which would cause urql to treat the subscription as changed.
 */
const SUBSCRIPTION_CONTEXT = { requestPolicy: "network-only" as const };

/**
 * Stable reducer for urql subscription — must be referentially stable
 * to prevent useSubscription from re-initialising each render.
 */
// biome-ignore lint/suspicious/noExplicitAny: generic subscription reducer used across types
const subscriptionReducer = (_prev: any, response: any) => response;

export const useShareVoiceSidebarSubscription = ({
  categories,
  enabled = true,
}: UseShareVoiceFilterSubscriptionProps = {}) => {
  const lastUpdateRef = useRef<string | null>(null);
  const { startDate, endDate, filtersAnd } =
    useShareOfVoiceParamsContextRequired();

  // Memoize variables so useSubscription receives a stable reference.
  // Without this, a new object is created every render → urql may restart
  // the subscription → state update → re-render → infinite loop.
  const variables = useMemo(
    () => ({
      projectionFilters: {
        dateRange: {
          startDate,
          endDate,
        },
        AND: filtersAnd.AND,
        state: "ADDED" as const,
      },
      categories,
    }),
    [startDate, endDate, filtersAnd.AND, categories],
  );

  const [result] = useSubscription(
    {
      query: ShareVoiceSidebarFilterSubscription,
      variables,
      pause: !enabled,
      context: SUBSCRIPTION_CONTEXT,
    },
    subscriptionReducer,
  );

  useEffect(() => {
    if (result.data?.shareVoiceSidebar) {
      const updateId = JSON.stringify(result.data.shareVoiceSidebar);

      if (lastUpdateRef.current === updateId) {
        return;
      }

      lastUpdateRef.current = updateId;
      updateFiltersViaSubscription(result.data.shareVoiceSidebar);
    }
  }, [result.data]);

  return result;
};
